import { orderBy, where } from "firebase/firestore";
import React, { useEffect } from "react";
import { Alert, Badge, Button, Card, Modal } from "react-bootstrap";
import { FaFileUpload, FaPlus } from "react-icons/fa";
import { TbSettingsExclamation } from "react-icons/tb";
import { Link, useNavigate, useParams } from "react-router";

import {
    useAreasDocument,
    useTaskCollection,
} from "../../api/firestoreWrappers.generated.jsx";
import { Breadcrumbs, breadcrumbPaths } from "../../components/Breadcrumbs";
import {
    CorvusList,
    CorvusListItem,
    CorvusListItemText,
} from "../../components/CorvusList";
import FileImporter from "../../components/FileImporter/index.jsx";
import Layout, { Title } from "../../components/Layout/Layout";
import { useUser } from "../../contexts/user_provider";
import urls from "../../urls.jsx";
import { naturalSort } from "../../utils/sortingUtils";
import { isTaskCommandValid } from "../TaskPage";

export function isTaskValid(task, areasDoc) {
    return !task?.commands?.some(
        (command) => !isTaskCommandValid(command, areasDoc),
    );
}

export default function TaskListPage() {
    const { organizationId, locationId } = useParams();
    const navigate = useNavigate();
    const { getUserIsAdmin } = useUser();
    const [tasks] = useTaskCollection(organizationId, locationId, [
        where("deleted", "==", false),
        orderBy("name", "asc"),
    ]);
    const [showEditExistingTaskModal, setShowEditExistingTaskModal] =
        React.useState(false);
    const [importedTask, setImportedTask] = React.useState(null);
    const [existingTask, setExistingTask] = React.useState(null);
    const [areasDoc] = useAreasDocument(organizationId, locationId);

    useEffect(() => {
        if (!locationId || locationId === "undefined") {
            navigate(urls.locations(organizationId), { replace: true });
        }
    }, [locationId, organizationId, navigate]);

    const handleFileImport = async (file) => {
        if (file) {
            try {
                const text = await file.text();
                const jsonData = JSON.parse(text);
                const taskId = jsonData?.id;
                const existingTask = tasks.find((task) => task.id === taskId);

                if (existingTask) {
                    setExistingTask(existingTask);
                    setImportedTask(jsonData);
                    setShowEditExistingTaskModal(true);
                } else {
                    navigate(urls.taskCreate(organizationId, locationId), {
                        state: { taskJson: jsonData },
                    });
                }
            } catch (error) {
                console.error("Failed to read file:", error);
            }
        }
    };

    const handleEditExistingTaskConfirm = () => {
        // Navigate to the task edit page
        navigate(
            urls.taskSettings(organizationId, locationId, existingTask.id),
            {
                state: { taskJson: importedTask },
            },
        );
    };

    const handleEditExistingTaskCancel = () => {
        // Close the modal without any action
        setShowEditExistingTaskModal(false);
        setExistingTask(null);
        setImportedTask(null);
    };

    return (
        <>
            <Modal
                show={showEditExistingTaskModal}
                onHide={handleEditExistingTaskCancel}
            >
                <Modal.Header closeButton>
                    <Modal.Title>Import Task</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    This imported task will edit the existing task{" "}
                    <strong>{existingTask?.name}</strong>. Do you want to
                    continue?
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={handleEditExistingTaskCancel}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleEditExistingTaskConfirm}
                    >
                        Continue
                    </Button>
                </Modal.Footer>
            </Modal>
            <Layout>
                <title>Tasks | MyCorvus</title>
                <Breadcrumbs path={breadcrumbPaths.taskList} />
                <Card>
                    <Title
                        backButtonUrl={urls.location(
                            organizationId,
                            locationId,
                        )}
                        title="Tasks"
                    >
                        {getUserIsAdmin() && (
                            <FileImporter
                                accept=".json"
                                onImport={handleFileImport}
                            >
                                <Button
                                    className="ms-2 mb-1"
                                    variant="outline-secondary"
                                >
                                    <FaFileUpload className="me-2" />
                                    Import Task
                                </Button>
                            </FileImporter>
                        )}
                        <Button
                            className="ms-2 mb-1"
                            variant="outline-success"
                            as={Link}
                            to={urls.taskCreate(organizationId, locationId)}
                        >
                            <FaPlus className="me-2" />
                            New Task
                        </Button>
                    </Title>
                    {tasks.length === 0 && (
                        // TODO : Move this functionalty to a component and reuse it in DroneListPage & RoutineListPage
                        <Alert variant="primary" className="mx-4 mb-4">
                            No tasks
                        </Alert>
                    )}
                    {tasks.length > 0 && (
                        <CorvusList>
                            {tasks
                                .sort((a, b) => naturalSort(a.name, b.name))
                                .map((task) => {
                                    const hasCustomAppSettings =
                                        task.apps?.some(
                                            (app) =>
                                                Object.keys(app?.settings || {})
                                                    .length > 0,
                                        ) ?? false;
                                    return (
                                        <CorvusListItem
                                            className="p-0"
                                            key={task.id}
                                            as={Link}
                                            to={urls.task(
                                                organizationId,
                                                locationId,
                                                task.id,
                                            )}
                                        >
                                            <CorvusListItemText>
                                                {task.name}
                                                {getUserIsAdmin() &&
                                                    hasCustomAppSettings && (
                                                        <TbSettingsExclamation
                                                            size="20"
                                                            className="ms-2"
                                                        />
                                                    )}
                                                {!isTaskValid(
                                                    task,
                                                    areasDoc,
                                                ) && (
                                                    <Badge
                                                        bg="danger"
                                                        className="ms-1"
                                                        size="sm"
                                                    >
                                                        Invalid
                                                    </Badge>
                                                )}
                                            </CorvusListItemText>
                                        </CorvusListItem>
                                    );
                                })}
                        </CorvusList>
                    )}
                </Card>
            </Layout>
        </>
    );
}
